import { all, call, put, spawn, take, fork, delay, select } from "redux-saga/effects";
import { ReduxActionTypes } from "ee/constants/ReduxActionConstants";
import { MAIN_THREAD_ACTION } from "ee/workers/Evaluation/evalWorkerActions";
import log from "loglevel";
import { logJSVarMutationEvent } from "../sagas/PostEvaluationSagas";
import type { Channel } from "redux-saga";
import { storeLogs } from "../sagas/DebuggerSagas";
import type {
  BatchedJSExecutionData,
  BatchedJSExecutionErrors,
} from "ee/reducers/entityReducers/jsActionsReducer";
import type { TMessage } from "utils/MessageUtil";
import { MessageType } from "utils/MessageUtil";
import type { ResponsePayload } from "../sagas/EvaluationsSaga";
import {
  evalWorker,
  executeTriggerRequestSaga,
  updateDataTreeHandler,
} from "../sagas/EvaluationsSaga";
import { logJSFunctionExecution } from "ee/sagas/JSFunctionExecutionSaga";
import { handleStoreOperations } from "./ActionExecution/StoreActionSaga";
import type {
  EvalTreeResponseData,
  JSVarMutatedEvents,
} from "workers/Evaluation/types";
import isEmpty from "lodash/isEmpty";
import type { UnEvalTree } from "entities/DataTree/dataTreeTypes";
import { sortJSExecutionDataByCollectionId } from "workers/Evaluation/JSObject/utils";
import type { LintTreeSagaRequestData } from "plugins/Linting/types";
import { evalErrorHandler } from "./EvalErrorHandler";
import { getUnevaluatedDataTree } from "selectors/dataTreeSelectors";

import { throttle, debounce, isBoolean, get } from "lodash";
export interface UpdateDataTreeMessageData {
  workerResponse: EvalTreeResponseData;
  unevalTree: UnEvalTree;
}

export function* handleEvalWorkerRequestSaga(listenerChannel: Channel<any>) {
  while (true) {
    const request: TMessage<any> = yield take(listenerChannel);
    // if (get(request, "body.method", "") === "PROCESS_TRIGGER") {
    //   if (
    //     get(request, "body.data.trigger.payload.switchValue") &&
    //     (get(request, "body.data.trigger.payload.executeCondition") ===
    //       "true" ||
    //       get(request, "body.data.trigger.payload.executeCondition") === true)
    //   ) {
    //     yield spawn(handleEvalWorkerMessage, request);
    //   } else if (!get(request, "body.data.trigger.payload.switchValue")) {
    //     yield spawn(handleEvalWorkerMessage, request);
    //   }
    // } else {
    yield spawn(handleEvalWorkerMessage, request);
    // }
  }
}

export function* lintTreeActionHandler(message: any) {
  const { body } = message;
  const { data } = body;
  const { configTree, unevalTree } = data as LintTreeSagaRequestData;
  yield put({
    type: ReduxActionTypes.LINT_TREE,
    payload: {
      unevalTree,
      configTree,
    },
  });
}

export function* processLogsHandler(message: any) {
  const { body } = message;
  const { data } = body;
  yield call(storeLogs, data);
}

export function* processJSFunctionExecution(message: any) {
  const { body } = message;
  const {
    data: { JSExecutionData, JSExecutionErrors },
  } = body;
  const {
    sortedData,
    sortedErrors,
  }: {
    sortedData: BatchedJSExecutionData;
    sortedErrors: BatchedJSExecutionErrors;
  } = yield* sortJSExecutionDataByCollectionId(
    JSExecutionData,
    JSExecutionErrors,
  );
  if (!isEmpty(sortedData)) {
    yield put({
      type: ReduxActionTypes.SET_JS_FUNCTION_EXECUTION_DATA,
      payload: sortedData,
    });
  }
  if (!isEmpty(sortedErrors)) {
    yield put({
      type: ReduxActionTypes.SET_JS_FUNCTION_EXECUTION_ERRORS,
      payload: sortedErrors,
    });
  }
}


const lastExecutionTimes = new Map();
const debounceState = new Map<
  string,
  {
    message: any;
    lastUpdateTime: number;
    timerRunning: boolean;
  }
>();

export function* processTriggerHandler(message: any) {
  const { body, messageId } = message;
  const { data } = body;
  const { eventType, trigger, triggerMeta } = data;
  const {
    triggerSetting,
    switchValue,
    debounceAndThrottleTime,
    executeCondition,
  } = trigger.payload;

  const time = parseInt(debounceAndThrottleTime, 10) || 1000;
  const condition =
    executeCondition === "true" ||
    (isBoolean(executeCondition) && executeCondition);

  if (switchValue && !condition) {
    // message.error("执行条件被限制，需联系管理员配置");
    yield call(evalWorker.respond, message.messageId, {
      data: null,
      error: "执行条件被限制，需联系管理员配置".replace(/"/g, ''),
    });
    return;
  }

  if (switchValue && condition) {
    const triggerId = triggerMeta?.source?.id;

    if (triggerSetting === "throttle") {
      const now = Date.now();
      const lastExecution = lastExecutionTimes.get(triggerId) || 0;
      // 如果距离上次执行的时间小于节流时间，则跳过
      if (now - lastExecution < time) {
        yield call(evalWorker.respond, message.messageId, {
          data: null,
          message: "Throttled",
        });
        return;
      }
      // 更新最后执行时间
      lastExecutionTimes.set(triggerId, now);
      yield call(executeTrigger, message);
      return;
    } else if (triggerSetting === "debounce") {
      const now = Date.now();
      const prev = debounceState.get(triggerId);
      debounceState.set(triggerId, {
        message,
        lastUpdateTime: now,
        timerRunning: prev?.timerRunning ?? false,
      });
      // 如果 timer 没在运行，启动一个新的监听任务
      if (!prev?.timerRunning) {
        debounceState.set(triggerId, {
          ...debounceState.get(triggerId)!,
          timerRunning: true,
        });
        yield fork(function* () {
          while (true) {
            yield delay(time);
            const current = debounceState.get(triggerId);
            if (!current) break;
            const sinceLastUpdate = Date.now() - current.lastUpdateTime;
            if (sinceLastUpdate >= time) {
              yield call(executeTrigger, current.message);
              debounceState.delete(triggerId);
              break;
            }
          }
        });
      }
      // 因为processTriggerHandler接收到多次worker信息要放掉
      yield call(evalWorker.respond, messageId, {
        data: null,
        message: "debounced",
      });
    } else {
      yield call(executeTrigger, message);
    }
  } else {
    yield call(executeTrigger, message);
  }
}

function* executeTrigger(message: any) {
  const { body } = message;
  const { data } = body;
  const { eventType, trigger, triggerMeta } = data;
  const { messageType } = message;
  log.debug({ trigger: data.trigger });
  const result: ResponsePayload = yield call(
    executeTriggerRequestSaga,
    trigger,
    eventType,
    triggerMeta,
  );
  if (messageType === MessageType.REQUEST)
    yield call(evalWorker.respond, message.messageId, result);
}

export function* handleEvalWorkerMessage(message: TMessage<any>) {
  const { body } = message;
  const { data, method } = body;
  switch (method) {
    case MAIN_THREAD_ACTION.LINT_TREE: {
      yield call(lintTreeActionHandler, message);
      break;
    }
    case MAIN_THREAD_ACTION.PROCESS_LOGS: {
      yield call(processLogsHandler, message);
      break;
    }
    case MAIN_THREAD_ACTION.PROCESS_JS_FUNCTION_EXECUTION: {
      yield call(processJSFunctionExecution, message);
      break;
    }
    case MAIN_THREAD_ACTION.PROCESS_TRIGGER: {
      yield call(processTriggerHandler, message);
      break;
    }
    case MAIN_THREAD_ACTION.PROCESS_STORE_UPDATES: {
      yield call(handleStoreOperations, data);
      break;
    }
    case MAIN_THREAD_ACTION.LOG_JS_FUNCTION_EXECUTION: {
      yield call(logJSFunctionExecution, message);
      break;
    }
    case MAIN_THREAD_ACTION.PROCESS_BATCHED_TRIGGERS: {
      const batchedTriggers = data;
      yield all(
        batchedTriggers.map((data: any) => {
          const { eventType, trigger, triggerMeta } = data;
          return call(
            executeTriggerRequestSaga,
            trigger,
            eventType,
            triggerMeta,
          );
        }),
      );
      break;
    }
    case MAIN_THREAD_ACTION.UPDATE_DATATREE: {
      const { unevalTree, workerResponse } = data as UpdateDataTreeMessageData;
      const unEvalAndConfigTree: ReturnType<typeof getUnevaluatedDataTree> =
      yield select(getUnevaluatedDataTree);
      yield call(updateDataTreeHandler, {
        evalTreeResponse: workerResponse as EvalTreeResponseData,
        unevalTree,
        requiresLogging: false,
        configTree: unEvalAndConfigTree.configTree,
      });
      break;
    }

    case MAIN_THREAD_ACTION.PROCESS_JS_VAR_MUTATION_EVENTS: {
      const jsVarMutatedEvents: JSVarMutatedEvents = data;
      yield call(logJSVarMutationEvent, jsVarMutatedEvents);
    }
  }

  yield call(evalErrorHandler, data?.errors || []);
}
